import { resultSet } from '../utils/resultSet';

/**
 * The rail's state, resolved rather than stored.
 *
 * `ChatScreen` holds one slot — `{ messageId, sourceKey }` — and asks these two
 * functions what it means. Storing the rows themselves would mean holding a copy
 * that goes stale the moment the turn re-renders (a late `total_count`, a
 * paginated fetch), and then owning a sync problem nobody asked for.
 *
 * One slot, deliberately: two turns can never be open at once, and a turn
 * arriving mid-read never re-points the rail, because only a press does.
 */

/**
 * The turn behind an id, with its rows.
 *
 * Not `find` by id: the session API gives a question and its answer the SAME id
 * (discover keys the pair by one message_id), and the first match is the user
 * turn — which has no rows, so the rail refused to open for exactly the turns
 * whose chip was advertising a count. Of the twins, the one with a result set
 * is the one the chip was pressed on.
 */
const rowsBearingTurn = (messages, messageId) => {
  let fallback = null;
  for (let index = 0; index < (messages || []).length; index += 1) {
    const candidate = messages[index];
    if (!candidate || candidate.id !== messageId) continue;
    const rs = resultSet(candidate);
    if (rs) return { message: candidate, index, rs };
    if (!fallback) fallback = { message: candidate, index, rs: null };
  }
  return fallback;
};

/** What a Data chip produces. `null` when that turn has nothing to show. */
export const openRail = (messages, messageId, sourceKey) => {
  const found = rowsBearingTurn(messages, messageId);
  if (!found || !found.rs) return null;
  const named = sourceKey != null ? found.rs.outputs.find((o) => o.key === sourceKey) : null;
  return { messageId, sourceKey: (named || found.rs.outputs[0]).key };
};

/**
 * What the rail should render right now, or `null` for closed.
 *
 * Derived on every render from the array `ChatScreen` already owns, so a turn
 * that has been deleted, or a reload that replaced it, closes the rail without
 * an effect racing the render that removed it.
 */
export const railFor = (messages, rail) => {
  if (!rail || !rail.messageId) return null;
  const list = messages || [];
  const found = rowsBearingTurn(list, rail.messageId);
  if (!found || !found.rs) return null;
  const { message, index, rs } = found;
  // A pinned output that no longer exists — outputs re-keyed underneath us —
  // falls back to the turn's first rather than rendering an empty rail.
  const output = rs.outputs.find((o) => o.key === rail.sourceKey) || rs.outputs[0];
  return { message, resultSet: rs, output, question: questionBefore(list, index) };
};

/**
 * The question this answer was to, for the rail's header.
 *
 * The rail outlives the answer's position on screen — that is the point of it —
 * so rows with no question above them are unattributed. Read by walking back to
 * the nearest turn that is not the assistant's, because that is the only place
 * the transcript records what was asked.
 */
const questionBefore = (messages, index) => {
  for (let i = index - 1; i >= 0; i -= 1) {
    const candidate = messages[i];
    if (candidate && candidate.author_id !== 0 && typeof candidate.message === 'string') {
      return candidate.message;
    }
  }
  return '';
};
